import * as _ from "lodash";
import * as React from "react";
import ExtensionManager from "./ExtensionManager";
// Cache for extension objects to avoid re-collecting on every render
const extensionCache = new Map();
/**
 * Hook to get extension objects for a given register function.
 * This is the hook equivalent of the `extend` HOC.
 *
 * @param registerFunc - The register function (e.g., registerSettings)
 * @param staticElements - Optional static elements to merge with extensions
 * @param group - Optional group identifier for grouped extensions
 * @param addExtInfo - Whether to add extension info to the callback
 * @returns Array of collected extension objects
 */
export function useExtensionObjects(registerFunc, staticElements, group, addExtInfo) {
    const context = React.useContext(ExtensionContext);
    const cacheKey = `${registerFunc.name}:${group ?? "default"}`;
    // Register the UI API on first use
    React.useEffect(() => {
        ExtensionManager.registerUIAPI(registerFunc.name);
    }, [registerFunc.name]);
    return React.useMemo(() => {
        if (context === null) {
            return staticElements ?? [];
        }
        // Check cache first
        if (!extensionCache.has(cacheKey)) {
            const collected = [];
            context.apply(registerFunc.name, (extInfo, ...args) => {
                // Always pass extInfo - when addExtInfo is false, extInfo contains
                // the first registered argument (e.g., id), not extension metadata
                const res = registerFunc(group, extInfo, ...args);
                if (res !== undefined) {
                    collected.push(res);
                }
            }, addExtInfo);
            extensionCache.set(cacheKey, collected);
        }
        const cached = extensionCache.get(cacheKey);
        return [...(staticElements ?? []), ...cached];
    }, [context, cacheKey, staticElements, registerFunc, group, addExtInfo]);
}
export const ExtensionContext = React.createContext(null);
export const useExtensionContext = () => {
    const context = React.useContext(ExtensionContext);
    if (context === null) {
        throw new Error("useExtensionContext must be used within an ExtensionProvider");
    }
    return context;
};
/**
 * extension function. This function creates a wrapper around a component that
 * binds the extensions of a component to its props
 *
 * @export
 * @param {(React.ComponentClass<P & IExtensionProps>)} ComponentToWrap the component to wrap
 * @returns {React.ComponentClass<P>} the wrapper component
 */
export function extend(registerFunc, groupProp, addExtInfo) {
    ExtensionManager.registerUIAPI(registerFunc.name);
    const extensions = {};
    const updateExtensions = (props, context) => {
        extensions[props[groupProp]] = [];
        context.apply(registerFunc.name, (extInfo, ...args) => {
            const res = registerFunc(props[groupProp], extInfo, ...args);
            if (res !== undefined) {
                extensions[props[groupProp]].push(res);
            }
        }, addExtInfo);
    };
    return (ComponentToWrap) => {
        // tslint:disable-next-line:class-name
        return class __ExtendedComponent extends React.Component {
            static contextType = ExtensionContext;
            mObjects;
            UNSAFE_componentWillReceiveProps(nextProps) {
                if (this.props[groupProp] !== nextProps[groupProp]) {
                    if (extensions[nextProps[groupProp]] === undefined) {
                        updateExtensions(nextProps, this.context);
                    }
                }
                if (this.props.staticElements !== nextProps.staticElements) {
                    this.mObjects = undefined;
                }
            }
            render() {
                const { children, staticElements } = this.props;
                if (extensions[this.props[groupProp]] === undefined) {
                    updateExtensions(this.props, this.context);
                }
                if (this.mObjects === undefined) {
                    this.mObjects = [].concat(staticElements || [], extensions[this.props[groupProp]] || []);
                }
                const wrapProps = {
                    ..._.omit(this.props, ["staticElements", "group"]),
                    objects: this.mObjects,
                };
                return React.createElement(ComponentToWrap, wrapProps, children);
            }
        };
    };
}
